// D:\directiveai-react\frontend\src\components\AGTPromo\SlidingTestimonialCards.js - Fixed overflow on both sides

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import TestimonialCard from './TestimonialCard';

// Create sliding animations in opposite directions
const slideLeftToRight = keyframes`
  from { transform: translateX(-50%); }
  to { transform: translateX(0%); }
`;

const slideRightToLeft = keyframes`
  from { transform: translateX(0%); }
  to { transform: translateX(-50%); }
`;

const SlideTrack = styled(Box)(({ theme, direction, speed = 120 }) => ({
  display: 'flex',
  width: 'fit-content',
  animation: `${direction === 'ltr' ? slideLeftToRight : slideRightToLeft} ${speed}s linear infinite`,
  '&:hover': {
    animationPlayState: 'paused'
  }
}));

// Restore the original SlideContainer with proper left positioning
const SlideContainer = styled(Box)(({ theme }) => ({
  width: '100vw', // Keep 100vw for full width
  overflow: 'hidden',
  marginBottom: theme.spacing(2),
  padding: 0,
  position: 'relative',
  left: '50%', // This centers the container
  transform: 'translateX(-50%)', // This ensures proper centering
  boxSizing: 'border-box',
}));

const SlidingTestimonialCards = ({ testimonials, sectionId }) => {
  
  // Use useMemo to select and prepare testimonials for the section
  const rowsOfCards = useMemo(() => {
    // Filter testimonials for the current section
    const sectionTestimonials = testimonials.filter(t => t.section === sectionId);
    
    // Create a function for deterministic shuffling
    const deterministicShuffle = (array, seedModifier = 0) => {
      // Create a simple hash based on testimonial texts and a modifier as seed
      const seed = array.reduce((acc, card, i) => 
        acc + (card.text?.charCodeAt(0) || 0) * (i + 1), 0) + seedModifier;
      
      // Deterministic pseudo-random function
      const random = (max) => {
        const x = Math.sin(seed * 9999) * 10000;
        return Math.floor((x - Math.floor(x)) * max);
      };
      
      // Shuffle deterministically
      const result = [...array];
      for (let i = result.length - 1; i > 0; i--) {
        const j = random(i + 1) % (i + 1);
        [result[i], result[j]] = [result[j], result[i]];
      }
      return result;
    };
    
    // Create 3 different shuffled arrays for the 3 rows
    const row1 = deterministicShuffle(sectionTestimonials, 1);
    const row2 = deterministicShuffle(sectionTestimonials, 2);
    const row3 = deterministicShuffle(sectionTestimonials, 3);
    
    // Ensure each row has enough cards (at least 8)
    const ensureEnoughCards = (arr) => {
      if (arr.length < 8) {
        return [...arr, ...arr].slice(0, 8);
      }
      return arr;
    };
    
    return [
      ensureEnoughCards(row1),
      ensureEnoughCards(row2),
      ensureEnoughCards(row3)
    ];
  }, [testimonials, sectionId]);
  
  // Determine slide directions and speeds for each row
  const rowConfigs = [
    { direction: 'ltr', speed: 120 },  // First row: left to right, normal speed
    { direction: 'rtl', speed: 100 },  // Second row: right to left, slightly faster
    { direction: 'ltr', speed: 140 }   // Third row: left to right, slower
  ];

  return (
    <Box sx={{ 
      width: '100%', 
      mt: 2, 
      mb: 4,
      position: 'relative',
      overflow: 'visible', // Allow overflow on both sides
    }}>
      {/* Three rows of testimonials with alternating directions and speeds */}
      {rowsOfCards.map((cards, rowIndex) => {
        const { direction, speed } = rowConfigs[rowIndex];
        const duplicatedCards = [...cards, ...cards]; // Duplicate cards for seamless looping
        
        return (
          <SlideContainer key={`row-${rowIndex}`}>
            <SlideTrack direction={direction} speed={speed}>
              {duplicatedCards.map((card, index) => (
                <Box key={`testimonial-${rowIndex}-${index}`} sx={{ px: 0.5 }}>
                  <TestimonialCard
                    text={card.text}
                    gradientColors={card.gradientColors}
                  />
                </Box>
              ))}
            </SlideTrack>
          </SlideContainer>
        );
      })}
    </Box>
  );
};

SlidingTestimonialCards.propTypes = {
  testimonials: PropTypes.array.isRequired,
  sectionId: PropTypes.string.isRequired
};

export default React.memo(SlidingTestimonialCards);